import { useState, useEffect, useMemo } from 'react';
import { t } from '@lingui/macro';
import styled from '@emotion/styled';
import { FilterRow as FilterRowType, formatDate, getNewFilterRow } from './transforms';
import { Number } from './Number';
import { DateFilter, DateProps, placeholder } from './DateFilter';
import type { TableFilter } from 'table/types';
import { colors } from 'styles/theme/colors';
import { Trash } from '@phosphor-icons/react';
import { Box } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useComboBoxOptions } from 'table/utils';
import { trans } from 'utils/trans';
import { GroupHeader } from 'components/inputs/ComboBox';
import { Fraction } from './Fraction';
import type { ChipProps } from 'components/chip/Chip';
import { Text } from 'components/text/Text';
import { Autocomplete } from 'components/inputs/Autocomplete';
import { TextInput } from 'components/inputs/TextInput';
import { DatePicker } from 'components/inputs/DatePicker';
import { IconButton } from 'components/button/IconButton';
import { ChipsList } from 'components/chip/ChipsList';
import { Avatar } from 'components/avatar/Avatar';
import { Select } from 'components/inputs/Select';

const normalizeFilterText = (text: string | undefined): string => {
  const trimmed = text?.replace(/\s+/g, ' ').trim() ?? '';
  return trimmed || '';
};

// Predefined options for switch/status filter types
// NOTE: Using a function to ensure translations are evaluated at render time, not module load time
const getSwitchFilterOptions = () => [
  { id: '1', label: t`Active` },
  { id: '0', label: t`Inactive` },
];

// Predefined options for boolean filter types (Yes/No)
const getBooleanFilterOptions = () => [
  { id: '1', label: t`Yes` },
  { id: '0', label: t`No` },
];

export const commenProps = {
  autoFocus: true,
  sx: {
    '&&.MuiFormControl-root': {
      maxWidth: '320px',
      minHeight: '44px',
    },
    '&&.MuiAutocomplete-root': {
      maxWidth: '320px',
      minHeight: '44px',
    },
    '&&.MuiInputBase-root': {
      maxWidth: '320px',
      width: '100%',
      padding: '10px 14px',
      margin: 0,
      font: 'unset',
    },
  },
  inputVariant: 'outlined',
  variant: 'outlined',
};

const filterTypeConstant = {
  text: { text: '' },
  description: { text: '' },
  tags: { text: '' },
  dd: { dd: [] },
  'tags-dd': { dd: [] },
  multiselect: { dd: [] },
  dateRange: { dateFrom: null, dateTo: null },
  date: null,
  number: { numberFrom: undefined, numberTo: undefined },
  fraction: { fractionFrom: undefined, fractionTo: undefined },
  status: { dd: [] },
  boolean: { dd: [] },
  switch: { dd: [] },
};

/**
 * Get the operator text based on filter type
 * - Text/description: "contains" (partial matching)
 * - Dropdowns/selects/status: "is" (exact match)
 * - Date/number ranges: "is between" (range selection)
 */
const getOperatorText = (filterType: TableFilter['type']): string => {
  switch (filterType) {
    case 'text':
    case 'description':
    case 'tags':
      return t`contains`;
    case 'dd':
    case 'tags-dd':
    case 'multiselect':
    case 'status':
    case 'boolean':
    case 'switch':
      return t`is`;
    case 'date':
    case 'dateRange':
    case 'number':
    case 'fraction':
      return t`is between`;
    default:
      return t`contains`;
  }
};

type FilterRowProps = {
  onRemove: () => void;
  data: FilterRowType;
  filterRows: FilterRowType[];
  filters: TableFilter[];
  selected: FilterRowType[];
  onChange: (data: FilterRowType, doUpdate?: boolean) => void;
  filterLabel?: string;
  tableId?: string;
};

export const FilterRow = ({
  onRemove,
  filterRows,
  data,
  filters,
  selected,
  onChange,
  filterLabel = t`Filter on:`,
  tableId,
}: FilterRowProps) => {
  const values = data;
  const column = values?.column || undefined;
  const filter = filters?.find(f => f.id === column) as TableFilter;

  const filterType = filter?.type as TableFilter['type'];
  const defaultValue = filterType ? filterTypeConstant[filterType] : {};
  const allOptions = useComboBoxOptions(filter?.api, filter?.options);

  useEffect(() => {
    onChange(values);
  }, []);

  const onUpdate = (vals: FilterRowType, doUpdate: boolean = false) => {
    onChange(vals, doUpdate);
  };
  const getDisplayLabel = (option: FilterRowType['dd'][number]): string => {
    const fullOption = allOptions.find(opt => String(opt.id) === String(option?.id));
    const customLabel =
      (option as { customLabel?: string })?.customLabel ||
      (fullOption as { customLabel?: string })?.customLabel;
    const showCustomLabel =
      (option as { showCustomLabel?: boolean })?.showCustomLabel ??
      (fullOption as { showCustomLabel?: boolean })?.showCustomLabel;
    return showCustomLabel && customLabel ? `${option?.label} (${customLabel})` : option?.label;
  };

  const autocompleteOptions = useMemo(
    () =>
      filters
        .filter(
          item => !selected.some(select => select?.column === item?.id && item?.id !== column)
        )
        .map(f => ({ id: f.id, label: trans(f.label) as string, group: f.group ?? '' })),
    [filters, selected, column]
  );

  const filterSelectOptions = useMemo(() => {
    if (filterType === 'boolean') return getBooleanFilterOptions();
    if (filterType === 'switch' || filterType === 'status') {
      return (
        filter?.options?.map(o => ({ id: String(o.id), label: o.label })) ||
        getSwitchFilterOptions()
      );
    }
    return [];
  }, [filterType, filter?.options]);

  return (
    <>
      <Row sx={{ gap: '24px' }} data-testid={`table_${tableId}_filter_row_${column}`}>
        <Row
          sx={{ overflow: 'hidden' }}
          style={{ overflowX: 'auto', paddingRight: '5px', flex: 1 }}
        >
          <Text
            variant="bodySm"
            weight="regular"
            right={16}
            data-testid={`table_${tableId}_filter_row_${column}_title`}
          >
            {filterLabel}
          </Text>
          <Autocomplete
            data-testid={`table_${tableId}_filter_row_where_${column}`}
            placeholder={t`Select column`}
            sx={{
              width: '100%',
              ...(filterType === 'dateRange' || filterType === 'number' || filterType === 'fraction'
                ? { maxWidth: '320px' }
                : { minWidth: '320px', maxWidth: '320px' }),
            }}
            variant="outlined"
            options={autocompleteOptions}
            groupBy={option => (option as { group?: string }).group ?? ''}
            renderGroup={params => (
              <li key={params.key}>
                {params.group && <GroupHeader>{params.group}</GroupHeader>}
                <ul style={{ padding: 0 }}>{params.children}</ul>
              </li>
            )}
            value={
              column
                ? filters
                    ?.map(f => ({ id: f.id, label: trans(f.label) as string }))
                    ?.find(opt => opt.id === column) || null
                : null
            }
            onChange={(event, value, reason) => {
              if (reason === 'clear') {
                const resetRow = { ...getNewFilterRow(), id: values.id, column: '' };
                onUpdate(resetRow, true);
              } else if (value && typeof value === 'object' && 'id' in value) {
                onUpdate(
                  {
                    ...values,
                    column: value.id as string,
                    ...(defaultValue ?? {}),
                  },
                  true
                );
              }
            }}
          />
          {filterType && (
            <Text
              variant="bodySm"
              weight="regular"
              right={16}
              left={24}
              data-testid={`table_${tableId}_filter_row_operator_${column}_title`}
            >
              {getOperatorText(filterType)}
            </Text>
          )}
          {filterType === 'text' || filterType === 'description' || filterType === 'tags' ? (
            <TextInput
              {...commenProps}
              placeholder={t`Enter your text here`}
              variant="outlined"
              fullWidth
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  onUpdate({ ...values, text: normalizeFilterText(values?.text) }, true);
                }
              }}
              value={values?.text}
              onChange={e => onUpdate({ ...values, text: e.target.value })}
              onBlur={() => onUpdate({ ...values, text: normalizeFilterText(values?.text) }, true)}
              data-testid={`table_${tableId}_filter_row_contains_${column}`}
            />
          ) : null}
          {filterType === 'dateRange' ? (
            <DateFilter
              {...(commenProps as Omit<DateProps, 'onChange'>)}
              isStopPropagationOnKeyDown
              from={values?.dateFrom}
              to={values?.dateTo}
              onChange={(from, to, doUpdate) => {
                onUpdate({ ...values, dateFrom: from, dateTo: to }, doUpdate);
              }}
              data-testid={`table_${tableId}_filter_row_contains_${column}`}
            />
          ) : null}
          {filterType === 'date' ? (
            <DatePicker
              {...(commenProps as Omit<DateProps, 'onChange'>)}
              id="filter-date"
              placeholder={placeholder}
              initialDate={formatDate(values?.date)}
              isStopPropagationOnKeyDown
              onBlur={(date, stringDate, isValidDate) => {
                if (!isValidDate && stringDate) return;
                onUpdate({ ...values, date: date?.toDate() }, true);
              }}
              data-testid={`table_${tableId}_filter_row_contains_${column}`}
            />
          ) : null}
          {filterType === 'number' ? (
            <Number
              {...commenProps}
              fromPlaceholder={filter?.fromPlaceholder}
              toPlaceholder={filter?.toPlaceholder}
              from={values?.numberFrom}
              to={values?.numberTo}
              onChange={(from, to) => onUpdate({ ...values, numberFrom: from, numberTo: to })}
              onBlur={() => onUpdate({ ...values }, true)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  onUpdate({ ...values }, true);
                }
              }}
              data-testid={`table_${tableId}_filter_row_contains_${column}`}
            />
          ) : null}
          {filterType === 'fraction' ? (
            <Fraction
              {...commenProps}
              fromPlaceholder={filter?.fromPlaceholder}
              toPlaceholder={filter?.toPlaceholder}
              from={values?.fractionFrom}
              to={values?.fractionTo}
              onChange={(from, to) => onUpdate({ ...values, fractionFrom: from, fractionTo: to })}
              onBlur={() => onUpdate({ ...values }, true)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  onUpdate({ ...values }, true);
                }
              }}
              data-testid={`table_${tableId}_filter_row_contains_${column}`}
            />
          ) : null}
          {(filterType === 'dd' || filterType === 'tags-dd' || filterType === 'multiselect') &&
          filter?.component
            ? filter.component(
                values?.dd,
                val => {
                  if (!val) {
                    return;
                  }
                  onUpdate({ ...values, dd: val ? [...val] : [] }, true);
                },
                `table_${tableId}_filter_row_contains_${column}`
              )
            : null}
          {(filterType === 'switch' || filterType === 'status' || filterType === 'boolean') && (
            <Select
              {...commenProps}
              placeholder={filterType === 'boolean' ? t`Select option` : t`Select status`}
              options={filterSelectOptions}
              value={values?.dd?.[0] ? String(values.dd[0].id) : ''}
              variant="outlined"
              sx={{ minWidth: '200px', maxWidth: '320px' }}
              onChange={(e: SelectChangeEvent<unknown>) => {
                const selectedId = e.target.value as string;
                const selectedOption = filterSelectOptions.find(opt => opt.id === selectedId);
                if (selectedOption) {
                  onUpdate({ ...values, dd: [selectedOption] }, true);
                }
              }}
            />
          )}
        </Row>
        {(filterRows.length !== 1 || filterType) && (
          <IconButton
            onClick={() => onRemove()}
            size="md"
            sx={{ marginLeft: 'auto' }}
            data-testid={`table_${tableId}_filter_row_remove_${column}`}
          >
            <Trash size={20} data-testid={`table_${tableId}_filter_row_remove_${column}_icon`} />
          </IconButton>
        )}
      </Row>
      {values?.dd?.length ? (
        <ChipsList
          data-testid={`table_${tableId}_filter_row_${column}_chip_list`}
          chips={values?.dd.map(s => ({
            size: 'md',
            'data-testid': `table_${tableId}_filter_row_${column}_chip_list_item_${s?.id}`,
            isTooltip: false,
            label: getDisplayLabel(s),
            ...(filter?.showChipAvatar
              ? {
                  avatar: (
                    <Avatar
                      letters={s.label}
                      size="xs"
                      color={s.customProps?.color ? `#${s.customProps.color}` : ''}
                      data-testid={`table_${tableId}_filter_row_${column}_chip_list_item_${s?.id}_avatar`}
                    />
                  ),
                }
              : {}),
            renderLabel: (label: ChipProps['label']) => (
              <Text
                variant="labelSm"
                color="brandPrimary"
                weight="semiBold"
                data-testid={`table_${tableId}_filter_row_${column}_chip_list_item_${s?.id}_title`}
              >
                {label}
              </Text>
            ),
            id: `${s.id}`,
            isBordered: false,
            background: colors.brand[100],
            deleteIconColor: colors.brand[300],
          }))}
          api={filters.find(f => f.id === column)?.api}
          onDelete={chip => {
            onUpdate(
              {
                ...values,
                dd: values.dd.filter(oldVal => String(oldVal.id) !== String(chip.id || '')),
              },
              true
            );
          }}
        />
      ) : null}
    </>
  );
};

const Row = styled(Box)`
  display: flex;
  flex-direction: row;
  white-space: nowrap;
  align-items: center;
`;
